// filepath: apps/web/src/features/cabinet/chrome/UserMenu.tsx
//
// FR-UI-CABINET-2 AC-4 — user menu dropdown.
//
// The avatar trigger is rendered by the parent (Topbar). This
// component is the dropdown panel that opens below the trigger.
//
// Two actions:
//   - "My Cabinet" — navigates to "/" (the cabinet landing).
//   - "Logout"     — calls signoutRedirect from react-oidc-context.
//                    post_logout_redirect_uri is already configured
//                    in apps/web/src/auth/oidcConfig.ts.
//
// The user's name + email come from the OIDC user object
// (auth.user.profile). Falls back gracefully when fields are
// missing (the OIDC token may not carry them).

import React, { useCallback, useEffect, useRef } from "react";
import { useAuth } from "react-oidc-context";
import { useTenant } from "@/tenant/TenantProvider";
import { useTranslation } from "@/i18n/useTranslation";
import { NEUTRAL, SPACE, RADIUS_MD } from "../styles";
import { navigate } from "../usePathname";
import { useMissingCount } from "../RequiredUserParameters/useMissingCount";

export interface UserMenuProps {
  open: boolean;
  onClose: () => void;
}

interface Identity {
  name: string;
  email: string;
  initial: string;
}

function pickIdentity(profile: unknown): Identity {
  const name =
    (typeof (profile as { name?: unknown })?.name === "string" &&
      (profile as { name: string }).name) ||
    (typeof (profile as { preferred_username?: unknown })
      ?.preferred_username === "string" &&
      (profile as { preferred_username: string }).preferred_username) ||
    "";
  const email =
    (typeof (profile as { email?: unknown })?.email === "string" &&
      (profile as { email: string }).email) ||
    "";
  const initial = name.length > 0 ? name.charAt(0).toUpperCase() : "?";
  return { name, email, initial };
}

export function UserMenu({
  open,
  onClose,
}: UserMenuProps): React.ReactElement | null {
  const auth = useAuth();
  const { tenantSlug } = useTenant();
  const { t } = useTranslation();
  const missingCount = useMissingCount();
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    function onPointerDown(event: PointerEvent): void {
      const target = event.target;
      if (
        target instanceof Node &&
        containerRef.current &&
        !containerRef.current.contains(target)
      ) {
        onClose();
      }
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  const handleCabinet = useCallback(() => {
    navigate("/");
    onClose();
  }, [onClose]);

  const handleRequiredParameters = useCallback(() => {
    navigate("/cabinet/required-parameters");
    onClose();
  }, [onClose]);

  const handleLogout = useCallback(() => {
    // signoutRedirect is wired in oidcConfig.ts with
    // post_logout_redirect_uri. The realm URL is the resolved
    // authority from the tenant config; passing an explicit
    // postLogoutRedirectUri is unnecessary because OIDC handles it.
    void auth.signoutRedirect();
    onClose();
  }, [auth, onClose]);

  if (!open) return null;
  const identity = pickIdentity(auth.user?.profile);
  void tenantSlug; // INV-1 — see useNavManifest.ts for rationale.

  return (
    <div
      ref={containerRef}
      role="menu"
      aria-label="User menu"
      data-testid="topbar-user-menu-panel"
      style={{
        position: "absolute",
        top: "calc(100% + 4px)",
        right: 0,
        minWidth: "var(--chrome-popup-min-width)",
        background: NEUTRAL.surfaceCard,
        border: `1px solid ${NEUTRAL.borderDefault}`,
        borderRadius: RADIUS_MD,
        boxShadow: "var(--shadow-md)",
        padding: `${SPACE[2]} 0`,
        zIndex: 100,
      }}
    >
      <div
        style={{
          padding: `${SPACE[2]} ${SPACE[4]}`,
          borderBottom: `1px solid ${NEUTRAL.borderDefault}`,
        }}
      >
        <div
          style={{
            fontSize: "14px",
            fontWeight: 600,
            color: NEUTRAL.textPrimary,
          }}
        >
          {identity.name || "User"}
        </div>
        {identity.email ? (
          <div
            style={{
              fontSize: "12px",
              color: NEUTRAL.textTertiary,
              marginTop: "2px",
            }}
          >
            {identity.email}
          </div>
        ) : null}
      </div>

      <button
        type="button"
        role="menuitem"
        data-testid="user-menu-my-cabinet"
        onClick={handleCabinet}
        style={{
          display: "block",
          width: "100%",
          padding: `${SPACE[2]} ${SPACE[4]}`,
          background: "transparent",
          border: 0,
          cursor: "pointer",
          fontFamily: "system-ui, sans-serif",
          fontSize: "14px",
          color: NEUTRAL.textSecondary,
          textAlign: "left",
        }}
      >
        My Cabinet
      </button>

      <button
        type="button"
        role="menuitem"
        data-testid="user-menu-required-parameters"
        onClick={handleRequiredParameters}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          padding: `${SPACE[2]} ${SPACE[4]}`,
          background: "transparent",
          border: 0,
          cursor: "pointer",
          fontFamily: "system-ui, sans-serif",
          fontSize: "14px",
          color: NEUTRAL.textSecondary,
          textAlign: "left",
        }}
      >
        <span>
          {t(
            "cabinet.required_user_parameters.menu_label",
            "Required user parameters",
          )}
        </span>
        {missingCount >= 1 ? (
          <span
            data-testid="user-menu-required-parameters--missing-count"
            aria-label={t(
              "cabinet.required_user_parameters.missing_count",
              "{count} required parameters missing",
              { count: String(missingCount) },
            )}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "20px",
              height: "20px",
              padding: "0 6px",
              fontSize: "11px",
              fontWeight: 600,
              color: "var(--text-on-primary)",
              background: "var(--color-danger)",
              borderRadius: "var(--radius-full)",
            }}
          >
            {missingCount > 99 ? "99+" : String(missingCount)}
          </span>
        ) : null}
      </button>

      <button
        type="button"
        role="menuitem"
        data-testid="user-menu-logout"
        onClick={handleLogout}
        style={{
          display: "block",
          width: "100%",
          padding: `${SPACE[2]} ${SPACE[4]}`,
          background: "transparent",
          border: 0,
          cursor: "pointer",
          fontFamily: "system-ui, sans-serif",
          fontSize: "14px",
          color: NEUTRAL.textSecondary,
          textAlign: "left",
        }}
      >
        Logout
      </button>
    </div>
  );
}
